import { useState, useEffect, useCallback } from "react"
import { Box, CloseButton, Dialog, HStack, Image, Input, Portal, Spinner, Stack } from "@chakra-ui/react"
import { Text, toaster } from "@/components"
import { DealEmail, EmailAttachment } from "@/types"
import { useDealEmailAttachments } from "@/hooks"
import { cleanEmailHtml } from "./emailUtils"
import { uploadAttachmentToDropbox, searchCompaniesByName } from "@/api/companies"

type RenamePromptState = {
  name: string
  target: "company" | "deal"
} & ({ mode: "attachment"; attId: string; contentBytes: string } | { mode: "email" })

interface Props {
  email: DealEmail | null
  open: boolean
  onClose: () => void
  dealManagerEmail?: string
  companyFolderPath?: string | null
  dealFolderPath?: string | null
  companyName?: string
  /** When provided, skips the internal useDealEmailAttachments hook */
  externalAttachments?: { data: EmailAttachment[]; isLoading: boolean }
  /** When provided, renders a purple "Stage as 409A" button per attachment */
  onStageAs409A?: (att: { name: string; contentType: string; contentBytes: string }) => void
  stagingInProgress?: boolean
  /** When provided, overrides the default filename in the rename-before-upload prompt (e.g. proof file convention) */
  proofFilePrefix?: string | null
  /** Upload full email as .eml to Dropbox */
  onUploadEmail?: (target: "company" | "deal", fileName: string, folderPath?: string) => Promise<void>
  /** Stage full email as 409A */
  onStageEmailAs409A?: () => Promise<void>
}

export const EmailDetailModal = ({
  email, open, onClose, dealManagerEmail, companyFolderPath, dealFolderPath, companyName,
  externalAttachments, onStageAs409A, stagingInProgress, proofFilePrefix,
  onUploadEmail, onStageEmailAs409A,
}: Props) => {
  const internalQuery = useDealEmailAttachments(
    !externalAttachments && open && email?.hasAttachments ? email.graphMessageId : null
  )
  const [expandedImg, setExpandedImg] = useState<string | null>(null)
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const [renamePrompt, setRenamePrompt] = useState<RenamePromptState | null>(null)
  const [emailActionDialog, setEmailActionDialog] = useState<{ action: "company" | "deal" | "stage" } | null>(null)
  const [emailUploading, setEmailUploading] = useState(false)

  // Company/folder selector state for "Co. Folder" uploads
  const [uploadCompanySearch, setUploadCompanySearch] = useState("")
  const [uploadCompanyResults, setUploadCompanyResults] = useState<Array<{ id: number; name: string; folderPath?: string | null }>>([])
  const [uploadSelectedCompany, setUploadSelectedCompany] = useState<{ id: number; name: string } | null>(null)
  const [uploadFolderPath, setUploadFolderPath] = useState("")
  const [showUploadCompanyDropdown, setShowUploadCompanyDropdown] = useState(false)

  // Debounced company search for the upload dialog
  useEffect(() => {
    if (!uploadCompanySearch || uploadCompanySearch.length < 2) {
      setUploadCompanyResults([])
      return
    }
    const timer = setTimeout(async () => {
      try {
        const results = await searchCompaniesByName(uploadCompanySearch)
        setUploadCompanyResults(results.slice(0, 10))
      } catch {
        setUploadCompanyResults([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [uploadCompanySearch])

  const handleSelectUploadCompany = useCallback((company: { id: number; name: string; folderPath?: string | null }) => {
    setUploadSelectedCompany({ id: company.id, name: company.name })
    setUploadCompanySearch(company.name)
    setShowUploadCompanyDropdown(false)
    setUploadFolderPath(company.folderPath || "")
  }, [])

  // Initialize company/folder state when rename prompt opens for "company" target
  const openCompanyRenamePrompt = useCallback((state: RenamePromptState) => {
    setUploadCompanySearch(companyName || "")
    setUploadSelectedCompany(companyName ? { id: 0, name: companyName } : null)
    setUploadFolderPath(companyFolderPath || "")
    setRenamePrompt(state)
  }, [companyName, companyFolderPath])

  if (!email) return null

  // Determine sender type: if dealManagerEmail provided, use exact match; otherwise detect ESO domain
  const isFromManager = dealManagerEmail
    ? email.fromAddress.toLowerCase() === dealManagerEmail.toLowerCase()
    : email.fromAddress.toLowerCase().includes("esofund.com")
  const senderLabel = isFromManager ? (dealManagerEmail ? "You" : "ESO") : "Client"
  const senderColor = isFromManager ? "#22c55e" : "#3b82f6"

  const attachmentsData = externalAttachments?.data ?? internalQuery.data ?? []
  const attachmentsLoading = externalAttachments?.isLoading ?? internalQuery.isLoading

  const imageAttachments = attachmentsData.filter((a) => a.isImage)
  const pdfAttachments = attachmentsData.filter((a) => a.contentType === "application/pdf")
  const otherAttachments = attachmentsData.filter((a) => !a.isImage && a.contentType !== "application/pdf")
  const hasUploadTarget = !!companyFolderPath || !!dealFolderPath
  const hasAnyAction = hasUploadTarget || !!onStageAs409A || !!onUploadEmail || !!onStageEmailAs409A
  const hasAttachments = attachmentsData.length > 0

  /** Build proof-convention filename from prefix + original extension */
  const getUploadName = (attName: string) => {
    if (!proofFilePrefix) return attName
    const dotIdx = attName.lastIndexOf(".")
    const ext = dotIdx >= 0 ? attName.slice(dotIdx) : ""
    return `${proofFilePrefix}${ext}`
  }

  const getEmailFileName = () => {
    if (proofFilePrefix) return `${proofFilePrefix}.eml`
    const subject = email.subject || "email"
    return `${subject.replace(/[/\\:*?"<>|]/g, "_").slice(0, 80)}.eml`
  }

  /** Handle attachment upload to Dropbox (existing flow) */
  const handleAttachmentUpload = async (fileName: string, contentBytes: string, target: "company" | "deal") => {
    const folderPath = target === "deal" ? dealFolderPath : (uploadFolderPath || companyFolderPath)
    if (!folderPath) return
    const name = target === "company" ? (uploadSelectedCompany?.name || companyName) : companyName
    setUploadingId("uploading")
    try {
      await uploadAttachmentToDropbox({ contentBytes, fileName, folderPath, companyName: name })
      toaster.success({ title: "Uploaded", description: `${fileName} uploaded to ${target === "deal" ? "deal" : "company"} folder` })
    } catch {
      toaster.error({ title: "Upload failed" })
    } finally {
      setUploadingId(null)
      setRenamePrompt(null)
    }
  }

  /** Handle email upload to Dropbox (.eml) */
  const handleEmailUpload = async (fileName: string, target: "company" | "deal") => {
    if (!onUploadEmail) return
    const folderPath = target === "company" ? (uploadFolderPath || undefined) : undefined
    setEmailUploading(true)
    try {
      await onUploadEmail(target, fileName, folderPath)
      toaster.success({ title: "Uploaded", description: `${fileName} uploaded to ${target === "deal" ? "deal" : "company"} folder` })
    } catch {
      toaster.error({ title: "Upload failed" })
    } finally {
      setEmailUploading(false)
      setRenamePrompt(null)
    }
  }

  /** Handle rename prompt submission — dispatches to attachment or email upload */
  const handleRenameSubmit = () => {
    if (!renamePrompt) return
    if (renamePrompt.mode === "attachment") {
      handleAttachmentUpload(renamePrompt.name, renamePrompt.contentBytes, renamePrompt.target)
    } else {
      handleEmailUpload(renamePrompt.name, renamePrompt.target)
    }
  }

  /** Handle email-level button click */
  const handleEmailAction = (action: "company" | "deal" | "stage") => {
    if (hasAttachments && !attachmentsLoading) {
      // Has attachments — show choice dialog
      setEmailActionDialog({ action })
    } else {
      // No attachments — proceed directly
      handleDirectEmailAction(action)
    }
  }

  /** Proceed with the email action directly (no choice dialog) */
  const handleDirectEmailAction = (action: "company" | "deal" | "stage") => {
    if (action === "stage") {
      handleStageEmail()
    } else if (action === "company") {
      openCompanyRenamePrompt({ mode: "email", name: getEmailFileName(), target: action })
    } else {
      setRenamePrompt({ mode: "email", name: getEmailFileName(), target: action })
    }
  }

  /** Handle staging the full email */
  const handleStageEmail = async () => {
    if (!onStageEmailAs409A) return
    setEmailUploading(true)
    try {
      await onStageEmailAs409A()
    } catch {
      toaster.error({ title: "Failed to stage email" })
    } finally {
      setEmailUploading(false)
    }
  }

  /** Handle choice dialog selection — user picked an attachment */
  const handleChoiceAttachment = (att: EmailAttachment) => {
    const action = emailActionDialog?.action
    setEmailActionDialog(null)
    if (!action) return

    if (action === "stage") {
      if (onStageAs409A) {
        onStageAs409A({ name: att.name, contentType: att.contentType, contentBytes: att.contentBytes })
      }
    } else if (action === "company") {
      openCompanyRenamePrompt({
        mode: "attachment",
        attId: att.id,
        name: getUploadName(att.name),
        contentBytes: att.contentBytes,
        target: action,
      })
    } else {
      setRenamePrompt({
        mode: "attachment",
        attId: att.id,
        name: getUploadName(att.name),
        contentBytes: att.contentBytes,
        target: action,
      })
    }
  }

  /** Handle choice dialog selection — user picked "Full email" */
  const handleChoiceFullEmail = () => {
    const action = emailActionDialog?.action
    setEmailActionDialog(null)
    if (!action) return
    handleDirectEmailAction(action)
  }

  const isUploading = !!uploadingId || emailUploading || stagingInProgress

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(e) => { if (!e.open) onClose() }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="800px" maxH="85vh" overflow="hidden" display="flex" flexDirection="column">
              <Dialog.Header px={5} pt={4} pb={3} borderBottomWidth="1px" flexShrink={0}>
                <Stack gap={1}>
                  <HStack gap={2}>
                    <Text fontSize="xs" fontWeight="600" color={senderColor}>
                      {senderLabel}
                    </Text>
                    <Text fontSize="xs" color="text.muted">
                      {email.fromName || email.fromAddress}
                    </Text>
                    <Text fontSize="xs" color="text.muted" ml="auto">
                      {new Date(email.receivedAt).toLocaleString()}
                    </Text>
                  </HStack>
                  <Dialog.Title fontSize="md" lineHeight="1.3">
                    {email.subject || "(no subject)"}
                  </Dialog.Title>
                  {email.toAddresses.length > 0 && (
                    <Text fontSize="xs" color="text.muted">
                      To: {email.toAddresses.map((t) => t.name || t.address).join(", ")}
                    </Text>
                  )}
                  {email.ccAddresses && email.ccAddresses.length > 0 && (
                    <Text fontSize="xs" color="text.muted">
                      Cc: {email.ccAddresses.map((c) => c.name || c.address).join(", ")}
                    </Text>
                  )}
                </Stack>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>

              {/* Email-level action bar */}
              {hasAnyAction && (
                <HStack px={5} py={2} gap={2} borderBottomWidth="1px" borderColor="border.default" flexShrink={0}>
                  {(onUploadEmail || companyFolderPath) && (
                    <Box as="button" px={3} py={1} fontSize="xs" fontWeight="600" borderRadius="md" cursor="pointer"
                      bg="rgba(59,130,246,0.1)" color="#3b82f6" _hover={{ bg: "rgba(59,130,246,0.2)" }}
                      onClick={() => handleEmailAction("company")}
                      disabled={isUploading} opacity={isUploading ? 0.5 : 1}
                    >Co. Folder</Box>
                  )}
                  {dealFolderPath && (onUploadEmail || hasUploadTarget) && (
                    <Box as="button" px={3} py={1} fontSize="xs" fontWeight="600" borderRadius="md" cursor="pointer"
                      bg="rgba(34,197,94,0.1)" color="#22c55e" _hover={{ bg: "rgba(34,197,94,0.2)" }}
                      onClick={() => handleEmailAction("deal")}
                      disabled={isUploading} opacity={isUploading ? 0.5 : 1}
                    >Deal Folder</Box>
                  )}
                  {(onStageAs409A || onStageEmailAs409A) && (
                    <Box as="button" px={3} py={1} fontSize="xs" fontWeight="600" borderRadius="md" cursor="pointer"
                      bg="rgba(168,85,247,0.1)" color="#a855f7" _hover={{ bg: "rgba(168,85,247,0.2)" }}
                      onClick={() => handleEmailAction("stage")}
                      disabled={isUploading} opacity={isUploading ? 0.5 : 1}
                    >{isUploading ? "..." : "Stage 409A"}</Box>
                  )}
                  {emailUploading && <Spinner size="xs" />}
                </HStack>
              )}

              <Dialog.Body px={5} py={4} overflow="auto" flex={1}>
                {/* Email body */}
                <Box fontSize="sm" lineHeight="1.6" mb={4}>
                  {email.bodyContent ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: cleanEmailHtml(email.bodyContent, email.bodyContentType || "text")
                      }}
                      style={{ wordBreak: "break-word" }}
                    />
                  ) : (
                    <Text color="text.muted">(no content)</Text>
                  )}
                </Box>

                {/* Attachments section */}
                {email.hasAttachments && (
                  <Box borderTop="1px solid" borderColor="border.default" pt={3}>
                    <Text fontSize="xs" fontWeight="600" color="text.muted" mb={2}>
                      Attachments
                    </Text>

                    {attachmentsLoading ? (
                      <HStack gap={2} py={3}>
                        <Spinner size="sm" />
                        <Text fontSize="xs" color="text.muted">Loading attachments...</Text>
                      </HStack>
                    ) : attachmentsData.length === 0 ? (
                      <Text fontSize="xs" color="text.muted">No downloadable attachments.</Text>
                    ) : (
                      <Stack gap={3}>
                        {/* Image attachments as previews */}
                        {imageAttachments.length > 0 && (
                          <HStack gap={3} flexWrap="wrap">
                            {imageAttachments.map((att) => (
                              <Box key={att.id}>
                                <Box
                                  border="1px solid" borderColor="border.default" borderRadius="md"
                                  overflow="hidden" cursor="pointer"
                                  _hover={{ borderColor: "#3b82f6", opacity: 0.9 }}
                                  onClick={() => setExpandedImg(`data:${att.contentType};base64,${att.contentBytes}`)}
                                >
                                  <Image
                                    src={`data:${att.contentType};base64,${att.contentBytes}`}
                                    alt={att.name}
                                    maxH="160px" maxW="240px" objectFit="contain"
                                  />
                                  <Text fontSize="2xs" color="text.muted" px={2} py={1} borderTop="1px solid" borderColor="border.default" truncate>
                                    {att.name}
                                  </Text>
                                </Box>
                              </Box>
                            ))}
                          </HStack>
                        )}

                        {/* PDF attachments inline */}
                        {pdfAttachments.map((att) => (
                          <Box key={att.id}>
                            <HStack mb={1} gap={2}>
                              <Text fontSize="sm" fontWeight="medium">{att.name}</Text>
                              <Text fontSize="2xs" color="text.muted">
                                {att.size > 1_000_000 ? `${(att.size / 1_000_000).toFixed(1)} MB` : `${Math.round(att.size / 1_000)} KB`}
                              </Text>
                            </HStack>
                            <iframe
                              src={`data:application/pdf;base64,${att.contentBytes}`}
                              style={{ width: "100%", height: "400px", border: "1px solid var(--chakra-colors-border-default, #e2e8f0)", borderRadius: "6px" }}
                              title={att.name}
                            />
                          </Box>
                        ))}

                        {/* Other file attachments as download cards */}
                        {otherAttachments.map((att) => (
                          <HStack
                            key={att.id}
                            px={3} py={2} border="1px solid" borderColor="border.default"
                            borderRadius="md" gap={2}
                          >
                            <a
                              href={`data:${att.contentType};base64,${att.contentBytes}`}
                              download={att.name}
                              style={{ color: "#3b82f6", textDecoration: "underline", fontSize: "14px" }}
                            >
                              {att.name}
                            </a>
                            <Text fontSize="2xs" color="text.muted" ml="auto">
                              {att.size > 1_000_000 ? `${(att.size / 1_000_000).toFixed(1)} MB` : `${Math.round(att.size / 1_000)} KB`}
                            </Text>
                          </HStack>
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Expanded image lightbox */}
      <Dialog.Root open={!!expandedImg} onOpenChange={(e) => { if (!e.open) setExpandedImg(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh" onClick={() => setExpandedImg(null)}>
              <Dialog.Body p={0} display="flex" justifyContent="center" alignItems="center">
                {expandedImg && (
                  <Image src={expandedImg} alt="Attachment" maxH="85vh" maxW="88vw" objectFit="contain" borderRadius="md" />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Rename prompt for upload */}
      <Dialog.Root open={!!renamePrompt} onOpenChange={(e) => { if (!e.open) setRenamePrompt(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="450px">
              <Dialog.Header px={5} pt={4} pb={2}>
                <Dialog.Title fontFamily="body" fontSize="md" fontWeight="600">
                  Upload to {renamePrompt?.target === "deal" ? "Deal" : "Company"} Folder
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body px={5} pb={5}>
                <Stack gap={3}>
                  {/* Company + Folder fields for company target */}
                  {renamePrompt?.target === "company" && (
                    <>
                      <Box position="relative">
                        <Text fontSize="xs" color="text.muted" mb={1}>Company</Text>
                        <Input
                          size="sm"
                          value={uploadCompanySearch}
                          onChange={(e) => {
                            setUploadCompanySearch(e.target.value)
                            setShowUploadCompanyDropdown(true)
                            if (e.target.value !== uploadSelectedCompany?.name) {
                              setUploadSelectedCompany(null)
                            }
                          }}
                          onFocus={() => setShowUploadCompanyDropdown(true)}
                          placeholder="Search company..."
                          borderColor={uploadSelectedCompany ? "green.600" : "border.default"}
                        />
                        {uploadSelectedCompany && (
                          <Text fontSize="2xs" color="green.400" mt={0.5}>
                            {uploadSelectedCompany.name}
                          </Text>
                        )}
                        {showUploadCompanyDropdown && uploadCompanyResults.length > 0 && (
                          <Box
                            position="absolute" top="100%" left={0} right={0} zIndex={10}
                            bg="card.bg" border="1px solid" borderColor="border.default"
                            borderRadius="md" maxH="160px" overflow="auto" mt={1}
                          >
                            {uploadCompanyResults.map((c) => (
                              <Box
                                key={c.id} px={3} py={2} cursor="pointer"
                                _hover={{ bg: "hover.bg" }}
                                onClick={() => handleSelectUploadCompany(c)}
                              >
                                <Text fontSize="sm">{c.name}</Text>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="text.muted" mb={1}>Dropbox Folder</Text>
                        <Input
                          size="sm"
                          value={uploadFolderPath}
                          onChange={(e) => setUploadFolderPath(e.target.value)}
                          placeholder="/Company Name"
                        />
                        {!uploadFolderPath && (
                          <Text fontSize="2xs" color="yellow.500" mt={0.5}>
                            No folder — file won't upload to Dropbox
                          </Text>
                        )}
                      </Box>
                    </>
                  )}

                  <Box>
                    <Text fontSize="xs" color="text.muted" mb={1}>File Name</Text>
                    <Input
                      size="sm"
                      value={renamePrompt?.name || ""}
                      onChange={(e) => setRenamePrompt((p) => p ? { ...p, name: e.target.value } : null)}
                    />
                  </Box>
                  {renamePrompt?.target !== "company" && (
                    <Text fontSize="2xs" color="text.muted">
                      Destination: {renamePrompt?.target === "deal" ? dealFolderPath : companyFolderPath}/
                    </Text>
                  )}
                  <HStack gap={2} justifyContent="flex-end">
                    <Box
                      as="button" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
                      border="1px solid" borderColor="border.default" color="text.muted"
                      _hover={{ bg: "hover.bg" }}
                      onClick={() => setRenamePrompt(null)}
                    >
                      Cancel
                    </Box>
                    <Box
                      as="button" px={3} py={1.5} borderRadius="md" fontSize="xs" fontWeight="600" cursor="pointer"
                      bg="#3b82f6" color="white"
                      _hover={{ opacity: 0.9 }}
                      onClick={handleRenameSubmit}
                      disabled={!renamePrompt?.name.trim() || isUploading || (renamePrompt?.target === "company" && !uploadFolderPath)}
                      opacity={!renamePrompt?.name.trim() || isUploading || (renamePrompt?.target === "company" && !uploadFolderPath) ? 0.5 : 1}
                    >
                      {isUploading ? "Uploading..." : "Upload"}
                    </Box>
                  </HStack>
                </Stack>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* Choice dialog — shown when email has attachments and user clicks an action button */}
      <Dialog.Root open={!!emailActionDialog} onOpenChange={(e) => { if (!e.open) setEmailActionDialog(null) }}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="card.bg" maxW="450px">
              <Dialog.Header px={5} pt={4} pb={2}>
                <Dialog.Title fontFamily="body" fontSize="md" fontWeight="600">
                  {emailActionDialog?.action === "stage"
                    ? "Stage as 409A"
                    : `Upload to ${emailActionDialog?.action === "deal" ? "Deal" : "Company"} Folder`}
                </Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body px={5} pb={5}>
                <Stack gap={2}>
                  <Text fontSize="sm" color="text.muted" mb={1}>
                    This email has attachments. What would you like to {emailActionDialog?.action === "stage" ? "stage" : "upload"}?
                  </Text>

                  {/* Per-attachment options */}
                  {attachmentsData.map((att) => (
                    <Box
                      key={att.id}
                      as="button"
                      w="100%"
                      textAlign="left"
                      px={3} py={2}
                      border="1px solid" borderColor="border.default" borderRadius="md"
                      cursor="pointer"
                      _hover={{ bg: "hover.bg" }}
                      onClick={() => handleChoiceAttachment(att)}
                    >
                      <HStack gap={2}>
                        <Text fontSize="sm" fontWeight="500" flex={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                          {att.name}
                        </Text>
                        <Text fontSize="2xs" color="text.muted" flexShrink={0}>
                          {att.size > 1_000_000 ? `${(att.size / 1_000_000).toFixed(1)} MB` : `${Math.round(att.size / 1_000)} KB`}
                        </Text>
                      </HStack>
                    </Box>
                  ))}

                  {/* Full email option */}
                  {(onUploadEmail || onStageEmailAs409A) && (
                    <Box
                      as="button"
                      w="100%"
                      textAlign="left"
                      px={3} py={2}
                      border="1px solid" borderColor="border.default" borderRadius="md"
                      cursor="pointer"
                      bg="rgba(168,85,247,0.05)"
                      _hover={{ bg: "rgba(168,85,247,0.1)" }}
                      onClick={handleChoiceFullEmail}
                    >
                      <Text fontSize="sm" fontWeight="500" color="#a855f7">
                        Full email (.eml)
                      </Text>
                      <Text fontSize="2xs" color="text.muted">
                        Includes email body and all attachments
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
